import { React, useState, useEffect, useRef } from "react";
import { confirmAlert } from "react-confirm-alert";
import Modal from "react-modal";
import PoPopupList from "./PoPopupList";
import { useNavigate } from "react-router-dom";
import PoConfirmDelete from "./PoConfirmDelete";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../constants";

// localStorage key used to signal (across browser tabs) that a PO was just
// created from the Job's PO tab and should be auto-imported into the job.
const NEW_PO_SIGNAL_KEY = "arc_job_new_po";

const JobPoes = ({ job, setJob, formDisabled, setModified }) => {
  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
    },
  };
  const navigate = useNavigate();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [poDeleteModalIsOpen, setPoDeleteModelIsOpen] = useState(false);
  const [poTotal, setPoTotal] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Keep a live reference to the latest job so the cross-tab storage handler
  // (registered once) always reads current values instead of a stale closure.
  const jobRef = useRef(job);
  useEffect(() => {
    jobRef.current = job;
  }, [job]);

  // Fetch a freshly created PO by its id and add it to this job. The PO was
  // created explicitly for this job, so we import it directly (one row per
  // package) rather than depending on a location match like manual import.
  const autoImportPo = async (poId) => {
    const currentJob = jobRef.current;
    try {
      const res = await axios.get(`${BASE_URL}/cargo/${poId}`);
      const cargo = res.data;
      const { packages, ...cargoInfo } = cargo || {};
      const newPoes = (packages || [])
        .filter(
          (pkg) => !currentJob.poes.some((y) => y.package?.id === pkg.id)
        )
        .map((pkg) => ({
          ...cargoInfo,
          id: cargo.id,
          poNo: cargo.poNo,
          vesselName: cargo.vesselName,
          supplierName: cargo.supplierName,
          package: pkg,
        }));
      if (newPoes.length === 0) {
        toast.info(
          "PO created, but it has no package to import yet. Add a package to the PO, then use Import PO."
        );
        return;
      }
      setModified(true);
      setJob((prev) => ({
        ...prev,
        poes: [...prev.poes, ...newPoes],
        packages: [
          ...prev.packages,
          ...newPoes.map((p) => ({ cargoId: p.id, packageID: p.package.id })),
        ],
      }));
      toast.success("New PO added to the job. Click Save to confirm.");
    } catch {
      toast.error("Could not auto-import the new PO. Please use Import PO.");
    }
  };

  // Listen for the "PO created" signal raised by the PO tab (see PoDetails).
  // The storage event only fires in other tabs, i.e. this job tab.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== NEW_PO_SIGNAL_KEY || !e.newValue) return;
      try {
        const { jobId, poId } = JSON.parse(e.newValue);
        if (poId && jobId === jobRef.current.id) {
          autoImportPo(poId);
        }
      } catch {
        /* ignore malformed signal */
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  function openModal() {
    const movingEventTypeIds = [4, 3, 6, 10];
    const movingEvents = job.events.filter((event) =>
      movingEventTypeIds.includes(event.logisticsEventTypeId)
    );

    if (movingEvents && movingEvents.length > 0) {
      confirmAlert({
        title: "Potential Conflict: Existing Events Detected",
        customUI: ({ onClose }) => {
          return (
            <div className="p-6 bg-slate-100 rounded-lg shadow-md max-w-xl mx-auto font-sans">
              <div className="flex items-center mb-4">
                <span className="text-yellow-500 text-xl mr-2">⚠️</span>
                <h3 className="text-lg font-semibold text-red-600">Warning</h3>
              </div>
              <p className="text-gray-700">
                This job has already created events that may affect the PO.
                Please remove the existing events and try importing again.
              </p>

              <div className="mt-4">
                <p className="font-semibold text-gray-800 mb-1">
                  Effecting Events:
                </p>
                <ul className="list-disc list-inside text-gray-600">
                  {movingEvents.map((x, i) => (
                    <li key={i}>{x.eventName}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 text-right">
                <button
                  onClick={onClose}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-150"
                >
                  OK
                </button>
              </div>
            </div>
          );
        },
        buttons: [],
      });

      return;
    }

    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
    setPoDeleteModelIsOpen(false);
  }
  const deleteSelectedRows = () => {
    // if (job.packages.length > 1) {
    setPoDeleteModelIsOpen(true);
    // confirmAlert({
    //   title: "Confirm to Delete",
    //   message: "Are you sure to do this.",
    //   buttons: [
    //     {
    //       label: "Yes",
    //       onClick: () => {
    //         deletePo();
    //       },
    //     },
    //     {
    //       label: "No",
    //     },
    //   ],
    // });
  };

  const updatePackageDetails = (poes) => {
    let total = {
      totalQuantity: 0,
      totalChWeight: 0,
      totalVolume: 0,
      totalWeight: 0,
    };

    poes
      .filter((x) => x.package)
      .map((po) => {
        total.totalQuantity = total.totalQuantity + parseInt(po.package.colli);
        total.totalWeight =
          total.totalWeight + parseFloat(po.package.weight * po.package.colli);
        total.totalChWeight =
          total.totalChWeight +
          parseFloat(po.package?.chargableWeight * po.package.colli);
        total.totalVolume =
          total.totalVolume + parseFloat(po.package?.volume * po.package.colli);
      });

    setPoTotal(total);
  };

  useEffect(() => {
    if (job.poes && job.poes.length > 0) {
      updatePackageDetails(job.poes);
    }
  }, [job.poes]);

  const toggleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setSelectedRows(job.poes.map((po) => po.package?.id));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectAll(false);
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const deletePo = (poes) => {
    setModified(true);
    setPoDeleteModelIsOpen(false);
    const updatedPoes = job.poes.filter(
      (po) => !selectedRows.includes(po.package?.id)
    );
    const updatedPackages = job.packages.filter(
      (pkg) => !selectedRows.includes(pkg.packageID)
    );
    setJob({
      ...job,
      poes: updatedPoes,
      packages: updatedPackages,
      removedPoes: poes.map((x) => ({
        poId: x.id,
        packageId: x.package.id,
        location: x.package.location,
        movementStatus: parseInt(x.package.movementStatus),
      })),
    });
    setSelectedRows([]);
    updatePackageDetails(updatedPoes);
    setSelectAll(false);
  };

  return (
    <div className="mt-5 px-5 w-full">
      <div className="flex ml-auto w-1/3 mb-2 items-center gap-2">
        {job.jobType === 400 && (
          <button
            type="button"
            title="Create PO"
            className="inline-flex self-end disabled:opacity-30 w-full h-full bg-sky-600 px-2 py-1 gap-1 justify-center items-center border border-transparent shadow-sm text-xs rounded-md text-white font-bold"
            onClick={() =>
              window.open(`#/po/new?jobId=${job.id}`, "_blank")
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-5 h-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 6v12m6-6H6"
              />
            </svg>
            Create PO
          </button>
        )}
        <button
          type="submit"
          title="Import PO"
          className="inline-flex self-end disabled:opacity-30 w-full h-full bg-lime-600 px-2 py-1 gap-1 justify-center items-center border border-transparent shadow-sm text-xs rounded-md text-white font-bold"
          onClick={() => openModal()}
          disabled={formDisabled}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 6v12m6-6H6"
            />
          </svg>
          Import PO
        </button>
        <button
          type="submit"
          title="Delete Selected"
          className="inline-flex self-end disabled:opacity-30 w-full h-full bg-red-600 px-2 py-1 gap-1 justify-center items-center border border-transparent shadow-sm text-xs rounded-md text-white font-bold "
          onClick={deleteSelectedRows}
          disabled={selectedRows.length === 0 || formDisabled}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="white"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
            />
          </svg>
          Delete Selected
        </button>
      </div>
      <div class="max-h-[65vh] border-b border-t bg-gray-300 p-0 border-gray-300 overflow-auto">
        <table class="w-full h-full text-xs text-left text-gray-500 border rounded-lg shadow-md sm:rounded-lg">
          <thead class="text-xxs sticky znone z-10 top-0 border-gray-300 text-gray-700 uppercase bg-gray-200 rounded-t-lg">
            <tr>
              <th scope="col" class="py-3 px-2">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  disabled={formDisabled}
                  className="rounded-sm focus:ring-0"
                />
              </th>
              <th scope="col" class="py-3 px-2">
                PO Number
              </th>
              <th scope="col" class="py-3 px-2">
                Vessel
              </th>
              <th scope="col" class="py-3 px-2">
                Supplier
              </th>
              <th scope="col" class="py-3 px-2">
                Location
              </th>
              <th scope="col" class="py-3 px-2">
                Boxes
              </th>
              <th scope="col" class="py-3 px-2">
                Ch.weight(KG)
              </th>
              <th scope="col" class="py-3 px-2">
                Volume(CBM)
              </th>
              <th scope="col" class="py-3 px-2">
                Weight(KG)
              </th>
              <th scope="col" class="py-3 px-2">
                LxWxH
              </th>
            </tr>
          </thead>
          <tbody className="max-h-40 min-h-fit overflow-y-scroll">
            {job.poes &&
              job.poes.map((v, pid) => {
                if (v.package) {
                  const isSelected = selectedRows.includes(v.package.id);
                  return (
                    <tr class="bg-white border-b h-full">
                      <td
                        scope="row"
                        class="py-2 px-2 font-medium text-gray-900 whitespace-nowrap"
                        key={pid}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(v.package.id)}
                          className="rounded-sm focus:ring-0"
                          disabled={formDisabled}
                        />
                      </td>
                      <td
                        class="py-2 px-2 border-l text-cyan-500 underline cursor-pointer"
                        onClick={() => navigate(`/po/${v.id}`)}
                      >
                        {v.poNo}
                      </td>
                      <td class="py-2 px-2 border-l">{v.vesselName}</td>
                      <td class="py-2 px-2 border-l">{v.supplierName}</td>
                      <td class="py-2 px-2 border-l">{v.package?.location}</td>
                      <td class="py-2 px-2 border-l">{v.package?.colli}</td>
                      <td class="py-2 px-2 border-l">
                        {v.package?.chargableWeight?.toFixed(2)}
                      </td>
                      <td class="py-2 px-2 border-l">
                        {v.package?.volume?.toFixed(2)}
                      </td>
                      <td class="py-2 px-2 border-l">
                        {v.package?.weight?.toFixed(2)}
                      </td>
                      <td class="py-2 px-2 border-l">
                        {v.package?.length}X{v.package?.width}X
                        {v.package?.height}
                      </td>
                    </tr>
                  );
                }
              })}
            <tr className="text-xxs sticky znone bottom-0 bg-white text-gray-700 uppercase font-semibold">
              <td class="py-4 px-2"></td>
              <td class="py-1 px-2"></td>
              <td class="py-1 px-2"></td>
              <td class="py-4 px-2"></td>
              <td class="py-4 px-2">Total&nbsp;&nbsp;:</td>
              <td class="py-4 px-2">
                {poTotal?.totalQuantity}
                &nbsp;NOS
              </td>
              <td class="py-4 px-2">
                {poTotal?.totalChWeight.toFixed(3)}
                &nbsp;KG
              </td>
              <td class="py-4 px-2">
                {poTotal?.totalVolume.toFixed(3)}
                &nbsp;CBM
              </td>
              <td class="py-4 px-2">
                {poTotal?.totalWeight}
                &nbsp;KG
              </td>
              <td class="py-1 px-2"></td>
            </tr>
          </tbody>
        </table>
        <div className="w-full flex justify-end">
          <Modal
            isOpen={modalIsOpen}
            //onAfterOpen={afterOpenModal}
            onRequestClose={closeModal}
            style={customStyles}
            contentLabel="Example Modal"
            ariaHideApp={false}
          >
            <PoPopupList
              setJob={setJob}
              closeModal={closeModal}
              job={job}
              setModified={setModified}
            />
          </Modal>
          <Modal
            isOpen={poDeleteModalIsOpen}
            //onAfterOpen={afterOpenModal}
            onRequestClose={closeModal}
            style={customStyles}
            contentLabel="Example Modal"
            ariaHideApp={false}
          >
            <PoConfirmDelete
              data={job.poes.filter((x) =>
                selectedRows.includes(x.package?.id)
              )}
              onSave={deletePo}
              closeModal={closeModal}
            />
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default JobPoes;
